import { useCallback, useEffect, useMemo, useState } from 'react';
import { Check, Copy, HelpCircle, LifeBuoy } from 'lucide-react';
import { fetchFeatures } from '../api';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { buildSupportDetails, formatSupportReport } from './support/supportDiagnostics';

const TENANT_ID = process.env.REACT_APP_TENANT_ID?.trim().toLowerCase();
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Канал поддержки задаётся здесь, одной строкой на инсталляцию.
 *
 * Пусто — значит «уточните у администратора стенда», и это честнее выдуманного
 * адреса: контактов поддержки в конфигурации проекта нет ни одного, а
 * неправильный адрес в интерфейсе хуже отсутствующего.
 */
const SUPPORT_CHANNEL = '';

/**
 * Самодиагностика: вопросы, с которыми обращаются чаще всего, и то, что пользователь
 * может проверить сам, не дожидаясь ответа. Формулировки описывают реальное поведение
 * системы, а не общие слова, — иначе раздел превращается в украшение.
 */
const SELF_CHECKS = [
  {
    question: 'Звонок загрузился, но аналитики по нему нет',
    answer:
      'Откройте «Интеграции» → «Задания» и найдите звонок по дате. Статус «Пропущено» означает, что номер попал в «Фильтры номеров» или у сотрудника выключен тумблер «Анализ»; причина написана там же в жёлтой плашке. Статус «Ошибка» — это сбой распознавания или анализа, такой звонок лечится кнопкой «Переобработать».'
  },
  {
    question: 'Раздел пустой, хотя звонки есть',
    answer:
      'Сначала проверьте период в правом верхнем углу: он не сохраняется между входами и каждый раз начинается с текущего месяца. Затем снимите фильтры по отделу, сотруднику и типу звонка — они складываются друг с другом.'
  },
  {
    question: 'В «Жалобах» пусто, хотя жалобы в разговорах были',
    answer:
      'После загрузки нового пакета узлов анализа нужно выбрать признак жалобы: «Настройки» → «Узлы анализа» → поле с путём вида triage.call_flags.complaint. Пока он не выбран, раздел не видит ни одного звонка, разобранного пакетом.'
  },
  {
    question: 'Не приходят новые звонки из телефонии',
    answer:
      'Это диагностируется на стороне сервера скриптом check-telephony.sh — передайте администратору стенда сведения из блока ниже, они сокращают разбор вдвое.'
  }
];

const Support = ({ user }) => {
  const [apiStatus, setApiStatus] = useState('checking');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;

    // Намеренно обычный запрос к API, а не /healthz: на same-origin /healthz
    // отвечает nginx самого интерфейса, и проверка была бы всегда зелёной,
    // даже когда контейнер api лежит.
    fetchFeatures()
      .then(() => {
        if (!cancelled) setApiStatus('ok');
      })
      .catch(() => {
        if (!cancelled) setApiStatus('error');
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const details = useMemo(
    () =>
      buildSupportDetails({
        tenantId: TENANT_ID,
        backendUrl: BACKEND_URL,
        version: __APP_VERSION__,
        username: user?.username,
        apiStatus
      }),
    [user?.username, apiStatus]
  );

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(formatSupportReport(details));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Буфер обмена недоступен (http без localhost, отказ в разрешении) — молча
      // ничего не делаем: сведения и так видны на экране и их можно выделить мышью.
    }
  }, [details]);

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <LifeBuoy className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            Помощь и поддержка
          </CardTitle>
          <CardDescription>
            Частые вопросы и сведения, которые понадобятся при обращении.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {SUPPORT_CHANNEL
              ? `Написать в поддержку: ${SUPPORT_CHANNEL}`
              : 'Канал поддержки для этого стенда не настроен — уточните его у администратора стенда.'}
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <HelpCircle className="h-4 w-4 text-gray-500 dark:text-gray-400" />
            Что можно проверить самостоятельно
          </CardTitle>
          <CardDescription>Четыре самые частые причины обращений.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          {SELF_CHECKS.map(({ question, answer }) => (
            <div key={question}>
              <p className="text-sm font-medium text-gray-900 dark:text-white">{question}</p>
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">{answer}</p>
            </div>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Сведения для обращения</CardTitle>
          <CardDescription>
            Приложите их к письму — с ними разбор начинается сразу, без уточняющих вопросов.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <dl className="divide-y divide-gray-200 dark:divide-gray-700">
            {details.map(({ key, label, value }) => (
              <div key={key} className="flex flex-wrap justify-between gap-2 py-2">
                <dt className="text-sm text-gray-500 dark:text-gray-400">{label}</dt>
                <dd className="text-sm font-medium text-gray-900 dark:text-white break-all">{value}</dd>
              </div>
            ))}
          </dl>

          <Button variant="outline" size="sm" data-ev="support.details.copy" onClick={handleCopy}>
            {copied ? <Check className="h-4 w-4 mr-2" /> : <Copy className="h-4 w-4 mr-2" />}
            {copied ? 'Скопировано' : 'Скопировать сведения'}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default Support;
